import React, { FC, useEffect } from "react";
import { useUIContext } from "../../Unknown/UIContext";
import AppointmentRescheduleDialog from "../AppointmentRescheduleDialog";
import { defaultCalendarMonth } from "./constants";
import { Appointment, Center } from "./types";
import useAvailableTimeSlots from "./useAvailableTimeSlots";
import useCalendarView from "./useCalendarView";
import useCenterTimeSlots from "./useCenterTimeSlots";
import useRescheduleCalendar from "./useRescheduleCalendar";
import useRescheduleTimePicker from "./useRescheduleTimePicker";
import useStatAvailability from "./useStatAvailability";
import useSubmitDialog from "./useSubmitDialog";

export type AppointmentRescheduleDialogContainerProps = {
  isOpen: boolean;
  handleClose: () => void;
  appointment: Appointment | null;
  center: Center | null;
  handleChangeAppointmentStartedAt: (
    startCenterTimezone: Date,
  ) => void | Promise<void>;
};

const AppointmentRescheduleDialogContainer: FC<
  AppointmentRescheduleDialogContainerProps
> = ({
  isOpen,
  handleClose,
  appointment,
  center,
  handleChangeAppointmentStartedAt,
}) => {
  const { setAlert } = useUIContext();

  const {
    selectedTime,
    selectedDate,
    selectedMonth,
    overriddenTime,
    onDateChange,
    onTimeChange,
    onTimeOverride,
    onMonthChange,
    resetForm,
  } = useCalendarView(defaultCalendarMonth);

  const isStatAppointment = !!appointment?.is_stat;
  const requiredSpecialties = appointment?.radiologySpecialties ?? [];

  const { statCutoffs } = useStatAvailability({
    centerId: center?.id,
    monthDate: selectedMonth,
    requiredSpecialties,
    enabled: isStatAppointment,
  });

  const { isLoading, errorMessage, data, refetch } = useCenterTimeSlots({
    center,
    monthDate: selectedMonth,
  });

  const availableTimeSlots = useAvailableTimeSlots({
    monthDate: selectedMonth,
    center,
    appointment,
    centerTimeSlots: data,
    statCutoffs,
  });

  useEffect(() => {
    if (!errorMessage) return;

    setAlert({
      isShown: true,
      severity: "error",
      message: errorMessage,
    });
  }, [setAlert, errorMessage]);

  const calendarProps = useRescheduleCalendar({
    selectedDate,
    defaultCalendarMonth,
    isLoading,
    center,
    onDateChange,
    onMonthChange,
    refetchCenterTimeSlots: refetch,
    statCutoffs,
  });

  const timePickerProps = useRescheduleTimePicker({
    availableTimeSlots,
    selectedDate,
    selectedTime,
    onTimeChange,
  });

  const submitAppointmentRescheduleProps = useSubmitDialog({
    appointment,
    center,
    selectedDate,
    selectedTime,
    overriddenTime,
    handleClose,
    resetForm,
    handleChangeAppointmentStartedAt,
    onTimeOverride,
  });

  return (
    <AppointmentRescheduleDialog
      {...submitAppointmentRescheduleProps}
      isOpen={isOpen}
      calendarProps={calendarProps}
      timePickerProps={timePickerProps}
      overriddenTime={overriddenTime}
      onTimeOverride={onTimeOverride}
    />
  );
};

export default AppointmentRescheduleDialogContainer;
